import type {Context} from '../types';
import {
  createNewCMSBinding,
  isBoundToCMS,
  deleteDynBindProperty,
  getDateType,
  getExtendedBindingData,
} from '../utils/legacyBindings';
import {Expressions, type ExpressionPath} from '@packages/systems/wfdl';
import {ERecord, type Expression} from '@packages/systems/wfdl-ast';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';

const PLAIN_TEXT_EXPR_PATH: ExpressionPath = [{in: 'Record', at: 'children'}];

export default function plainTextCMSLegacyBindingsToUB(context: Context) {
  const {jsonNode} = context.node;
  const plainTextUB = getPlainTextUniversalBinding(context);

  const needed = plainTextUB !== null;

  return {
    needed,
    logData: {
      msg: 'Migrate Plain Text legacy CMS binding to Universal Bindings.',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      if (plainTextUB === null) {
        return;
      }

      deleteDynBindProperty(jsonNode, 'innerHTML');

      if (jsonNode.data?.children) {
        // eslint-disable-next-line webflow/no-delete
        delete jsonNode.data.children;
      }

      jsonNode.bind = Expressions.sparseSetIn(
        jsonNode.bind || ERecord({}),
        PLAIN_TEXT_EXPR_PATH,
        plainTextUB
      );
    },
  };
}

function getPlainTextUniversalBinding(context: Context): Expression | null {
  const {jsonNode} = context.node;
  const {data = {}} = jsonNode;

  // If there is no type on the node, run modified inlined migration that we run during reification to get the type from tag
  // This is done to ensure that we migrate all the required nodes without type but with a tag
  const jsonNodeType = jsonNode.type
    ? jsonNode.type
    : getJsonNodeTypeFromTag(jsonNode);

  if (
    jsonNodeType === 'Heading' ||
    jsonNodeType === 'Paragraph' ||
    jsonNodeType === 'Block' ||
    jsonNodeType === 'Blockquote' ||
    jsonNodeType === 'FormBlockLabel' ||
    jsonNodeType === 'FormInlineLabel' ||
    jsonNodeType === 'ListItem'
  ) {
    const dynBindInnerHTML = data.dyn?.bind?.innerHTML;
    const bindingData = dynBindInnerHTML
      ? getExtendedBindingData(dynBindInnerHTML)
      : false;

    if (bindingData && isBoundToCMS(context)) {
      const bindingDataType =
        bindingData.type === 'Date'
          ? getDateType(context, bindingData)
          : bindingData.type;
      return createNewCMSBinding(
        bindingDataType,
        'plainText',
        bindingData.slug,
        bindingData.filter
      );
    }
  }

  return null;
}

// Inlined from: packages/systems/core/utils/TagToTypeMigrationUtils/index.ts migrateJsonNodeFromTagToType
// The changes that have been made are:
// - Removed the creation of a new JSONNode as we are only interested in the type
// - Return type changed from JSONNode to string | null because we only care about the type
// - Changed getting of formType and commerceType to use optional chaining instead of lodash get
const getJsonNodeTypeFromTag = (jsonNode: JSONNode): string | null => {
  const {type, tag, data} = jsonNode;

  // If there is no tag, we can't determine the type.
  if (!tag) {
    return null;
  }

  // If we have a type we can skip all the checks.
  // This should not happen ever as we are calling this only if we don't have a type
  // but as this is inlined I wanted to make as few changes as possible.
  if (type) {
    return type;
  }

  const formType = data?.form?.type;
  const commerceType = data?.commerce?.type;

  // The symbol instance check should always be first, as there can be collisions (a symbol instance _is_ a div block)
  if (DomTypeUtils.isSymbolInst(data)) {
    return 'Symbol';
  }

  if (DomTypeUtils.isBackgroundVideoWrapper(data)) {
    return 'BackgroundVideoWrapper';
  }

  if (tag === 'base') {
    return 'Base';
  }

  if (tag === 'body') {
    return 'Body';
  }

  if (tag === 'wf-comment') {
    return 'Comment';
  }

  if (tag === 'wf-custom-code') {
    return 'CustomCode';
  }

  if (commerceType === 'add-to-cart-wrapper') {
    return 'CommerceAddToCartWrapper';
  }

  if (commerceType === 'add-to-cart-form') {
    return 'CommerceAddToCartForm';
  }

  if (commerceType === 'add-to-cart-quantity-input') {
    return 'CommerceAddToCartQuantityInput';
  }

  if (commerceType === 'add-to-cart-option-list') {
    return 'CommerceAddToCartOptionList';
  }

  if (commerceType === 'add-to-cart-option') {
    return 'CommerceAddToCartOption';
  }

  if (commerceType === 'add-to-cart-option-label') {
    return 'CommerceAddToCartOptionLabel';
  }

  if (commerceType === 'add-to-cart-option-select') {
    return 'CommerceAddToCartOptionSelect';
  }

  if (commerceType === 'add-to-cart-button') {
    return 'CommerceAddToCartButton';
  }

  if (commerceType === 'add-to-cart-out-of-stock') {
    return 'CommerceAddToCartOutOfStock';
  }

  if (commerceType === 'add-to-cart-error') {
    return 'CommerceAddToCartError';
  }

  if (commerceType === 'add-to-cart-error-text') {
    return 'CommerceAddToCartErrorText';
  }

  if (commerceType === 'cart-container-wrapper') {
    return 'CommerceCartContainerWrapper';
  }

  if (commerceType === 'cart-open-link') {
    return 'CommerceCartOpenLink';
  }

  if (commerceType === 'cart-open-link-count') {
    return 'CommerceCartOpenLinkCount';
  }

  if (commerceType === 'cart-open-link-image') {
    return 'CommerceCartOpenLinkImage';
  }

  if (commerceType === 'cart-wrapper') {
    return 'CommerceCartWrapper';
  }

  if (commerceType === 'cart-container') {
    return 'CommerceCartContainer';
  }

  if (commerceType === 'cart-header') {
    return 'CommerceCartHeader';
  }

  if (commerceType === 'cart-heading') {
    return 'CommerceCartHeading';
  }

  if (commerceType === 'cart-form-wrapper') {
    return 'CommerceCartFormWrapper';
  }

  if (commerceType === 'cart-form') {
    return 'CommerceCartForm';
  }

  if (commerceType === 'cart-empty-state') {
    return 'CommerceCartEmptyState';
  }

  if (commerceType === 'cart-error-state') {
    return 'CommerceCartErrorState';
  }

  if (commerceType === 'cart-list') {
    return 'CommerceCartList';
  }

  if (commerceType === 'cart-footer') {
    return 'CommerceCartFooter';
  }

  if (commerceType === 'cart-line-item') {
    return 'CommerceCartLineItem';
  }

  if (commerceType === 'cart-close-link') {
    return 'CommerceCartCloseLink';
  }

  if (commerceType === 'cart-remove-link') {
    return 'CommerceCartRemoveLink';
  }

  if (commerceType === 'cart-order-value') {
    return 'CommerceCartOrderValue';
  }

  if (commerceType === 'cart-checkout-button') {
    return 'CommerceCartCheckoutButton';
  }

  if (commerceType === 'cart-item') {
    return 'CommerceCartItem';
  }

  if (commerceType === 'cart-item-image') {
    return 'CommerceCartItemImage';
  }

  if (commerceType === 'cart-item-info') {
    return 'CommerceCartItemInfo';
  }

  if (commerceType === 'cart-product-name') {
    return 'CommerceCartProductName';
  }

  if (commerceType === 'cart-error-text') {
    return 'CommerceCartErrorText';
  }

  if (commerceType === 'cart-quantity') {
    return 'CommerceCartQuantity';
  }

  if (DomTypeUtils.isDynamoWrapper(data)) {
    return 'DynamoWrapper';
  }

  if (DomTypeUtils.isDynamoList(data)) {
    return 'DynamoList';
  }

  if (DomTypeUtils.isDynamoItem(data)) {
    return 'DynamoItem';
  }

  if (DomTypeUtils.isDynamoEmpty(data)) {
    return 'DynamoEmpty';
  }

  if (DomTypeUtils.isGridColumn(data)) {
    return 'Column';
  }

  if (DomTypeUtils.isGridContainer(data)) {
    return 'Container';
  }

  if (DomTypeUtils.isGridRow(data)) {
    return 'Row';
  }

  if (DomTypeUtils.isGridSection(data)) {
    return 'Section';
  }

  if (DomTypeUtils.isFacebookWidget(data)) {
    return 'Facebook';
  }

  if (DomTypeUtils.isGPlusWidget(data)) {
    return 'GooglePlus';
  }

  if (tag === 'head') {
    return 'Head';
  }

  if (tag === 'html') {
    return 'Document';
  }

  if (DomTypeUtils.isIconWidget(data)) {
    return 'Icon';
  }

  if (DomTypeUtils.isImage(tag)) {
    return 'Image';
  }

  if (tag === 'br') {
    return 'LineBreak';
  }

  if (DomTypeUtils.isMapWidget(data)) {
    return 'MapWidget';
  }

  if (tag === 'span') {
    return 'Span';
  }

  if (tag === 'figcaption') {
    return 'Figcaption';
  }

  if (tag === 'figure') {
    return 'Figure';
  }

  if (tag === 'strong') {
    return 'Strong';
  }

  if (tag === 'em') {
    return 'Emphasized';
  }

  if (tag === 'sup') {
    return 'Superscript';
  }

  if (tag === 'sub') {
    return 'Subscript';
  }

  if (tag === 'code') {
    return 'InlineCode';
  }

  if (tag === 'link') {
    return 'Resource';
  }

  if (tag === 'script') {
    return 'Script';
  }

  if (tag === 'style') {
    return 'Style';
  }

  if (DomTypeUtils.isSliderWrapper(data)) {
    return 'SliderWrapper';
  }

  if (DomTypeUtils.isSliderMask(data)) {
    return 'SliderMask';
  }

  if (DomTypeUtils.isSliderSlide(data)) {
    return 'SliderSlide';
  }

  if (DomTypeUtils.isSliderArrow(data)) {
    return 'SliderArrow';
  }

  if (DomTypeUtils.isSliderNav(data)) {
    return 'SliderNav';
  }

  if (tag === 'title') {
    return 'Title';
  }

  if (DomTypeUtils.isTwitterWidget(data)) {
    return 'Twitter';
  }

  if (DomTypeUtils.isVideo(data)) {
    return 'Video';
  }

  if (DomTypeUtils.isHtmlEmbed(data)) {
    return 'HtmlEmbed';
  }

  if (DomTypeUtils.isNavbarBrand(data)) {
    return 'NavbarBrand';
  }

  if (DomTypeUtils.isNavbarButton(data)) {
    return 'NavbarButton';
  }

  if (DomTypeUtils.isNavbarContainer(data)) {
    return 'NavbarContainer';
  }

  if (DomTypeUtils.isNavbarLink(data)) {
    return 'NavbarLink';
  }

  if (DomTypeUtils.isNavbarMenu(data)) {
    return 'NavbarMenu';
  }

  if (DomTypeUtils.isNavbarWrapper(data)) {
    return 'NavbarWrapper';
  }

  if (tag === 'meta') {
    return 'Meta';
  }

  if (DomTypeUtils.isBlockquote(tag)) {
    return 'Blockquote';
  }

  if (DomTypeUtils.isDropdownLink(data)) {
    return 'DropdownLink';
  }

  if (DomTypeUtils.isDropdownList(data)) {
    return 'DropdownList';
  }

  if (DomTypeUtils.isDropdownToggle(data)) {
    return 'DropdownToggle';
  }

  if (DomTypeUtils.isDropdownWrapper(data)) {
    return 'DropdownWrapper';
  }

  if (DomTypeUtils.isFormBlockLabel(data)) {
    return 'FormBlockLabel';
  }

  if (formType === 'recaptcha') {
    return 'FormReCaptcha';
  }

  if (DomTypeUtils.isFormButton(data)) {
    return 'FormButton';
  }

  if (DomTypeUtils.isFormCheckboxInput(data)) {
    return 'FormCheckboxInput';
  }

  if (DomTypeUtils.isFormCheckboxWrapper(data)) {
    return 'FormCheckboxWrapper';
  }

  if (DomTypeUtils.isFormErrorMessage(data)) {
    return 'FormErrorMessage';
  }

  if (DomTypeUtils.isFormForm(data)) {
    return 'FormForm';
  }

  if (DomTypeUtils.isFormInlineLabel(data)) {
    return 'FormInlineLabel';
  }

  if (DomTypeUtils.isFormRadioInput(data)) {
    return 'FormRadioInput';
  }

  if (DomTypeUtils.isFormRadioWrapper(data)) {
    return 'FormRadioWrapper';
  }

  if (DomTypeUtils.isFormSelect(data)) {
    return 'FormSelect';
  }

  if (DomTypeUtils.isFormSuccessMessage(data)) {
    return 'FormSuccessMessage';
  }

  if (formType === 'file-upload-input') {
    return 'FormFileUploadInput';
  }

  if (formType === 'file-upload') {
    return 'FormFileUploadWrapper';
  }

  if (formType === 'file-upload-default') {
    return 'FormFileUploadDefault';
  }

  if (formType === 'file-upload-uploading') {
    return 'FormFileUploadUploading';
  }

  if (formType === 'file-upload-uploading-btn') {
    return 'FormFileUploadUploadingBtn';
  }

  if (formType === 'file-upload-success') {
    return 'FormFileUploadSuccess';
  }

  if (formType === 'file-upload-file') {
    return 'FormFileUploadFile';
  }

  if (formType === 'file-upload-file-name') {
    return 'FormFileUploadFileName';
  }

  if (formType === 'file-upload-remove-link') {
    return 'FormFileUploadRemoveLink';
  }

  if (formType === 'file-upload-error') {
    return 'FormFileUploadError';
  }

  if (formType === 'file-upload-error-msg') {
    return 'FormFileUploadErrorMsg';
  }

  if (formType === 'file-upload-label') {
    return 'FormFileUploadLabel';
  }

  if (formType === 'file-upload-info') {
    return 'FormFileUploadInfo';
  }

  if (DomTypeUtils.isFormTextarea(data)) {
    return 'FormTextarea';
  }

  if (DomTypeUtils.isFormTextInput(data)) {
    return 'FormTextInput';
  }

  if (DomTypeUtils.__DEPRECATED__isFormWrapper(data)) {
    return 'FormWrapper';
  }

  if (DomTypeUtils.isSearchForm(data)) {
    return 'SearchForm';
  }

  if (DomTypeUtils.isSearchInput(data)) {
    return 'SearchInput';
  }

  if (DomTypeUtils.isSearchButton(data)) {
    return 'SearchButton';
  }

  if (DomTypeUtils.isSearchResultItem(data)) {
    return 'SearchResultItem';
  }

  if (DomTypeUtils.isSearchResultList(data)) {
    return 'SearchResultList';
  }

  if (DomTypeUtils.isSearchResultWrapper(data)) {
    return 'SearchResultWrapper';
  }

  if (DomTypeUtils.isSearchResultEmptyMessage(data)) {
    return 'SearchResultEmpty';
  }

  if (DomTypeUtils.isParagraph(tag)) {
    return 'Paragraph';
  }

  if (DomTypeUtils.isTabsContent(data)) {
    return 'TabsContent';
  }

  if (DomTypeUtils.isTabsLink(data)) {
    return 'TabsLink';
  }

  if (DomTypeUtils.isTabsMenu(data)) {
    return 'TabsMenu';
  }

  if (DomTypeUtils.isTabsPane(data)) {
    return 'TabsPane';
  }

  if (DomTypeUtils.isTabsWrapper(data)) {
    return 'TabsWrapper';
  }

  if (DomTypeUtils.isHeading(tag)) {
    return 'Heading';
  }

  if (DomTypeUtils.isLightboxWrapper(data)) {
    return 'LightboxWrapper';
  }

  if (DomTypeUtils.isLink(tag)) {
    return 'Link';
  }

  if (DomTypeUtils.isList(data)) {
    return 'List';
  }

  if (DomTypeUtils.isListItem(data)) {
    return 'ListItem';
  }

  if (DomTypeUtils.isRichBlock(tag, data)) {
    return 'RichText';
  }

  if (tag === 'iframe') {
    return 'Iframe';
  }

  // Fall back to `Block` for unknown nodes.
  return 'Block';
};

export const forTestSuite = {
  getJsonNodeTypeFromTag,
} as const;
